import type { DraftItem, TimetableDraft } from "@teamna/shared";

/**
 * The week editor's state: what parents see (`base`), the draft, and the steps back. A pure reducer,
 * so every change goes through one place and every change can be undone one step at a time.
 *
 * The draft lives only in this browser until Publish. A small module store, backed by the browser's
 * storage, keeps it while the owner visits another page or reloads, as long as the published
 * timetable has not changed meanwhile.
 */

export type Step = { label: string; draft: TimetableDraft };

export type EditorState = {
  stamp: string;
  base: TimetableDraft;
  draft: TimetableDraft;
  history: Step[];
  view: "week" | "dates";
  /**
   * The level being added to the week, and for free hours the person they are for. It stays picked
   * after a click, while a block is open and during a drag: the owner adds time after time, then
   * goes to the next level (prototype `teamna/prototypes/timetable-flow-v2.html`).
   */
  brush: { groupId: string; staffUserId: string | null } | null;
  /** The block open in the card next to the week. */
  selected: string | null;
  /** The time added last with the picked level: its other days show dashed, one click adds a day. */
  last: string | null;
  /** The item the owner touched last: a problem's fix goes to it. */
  touched: string | null;
  adding: boolean;
  /** The date open on the Dates view. */
  day: string | null;
  /** `yyyy-MM` on the Dates view. */
  month: string;
  review: boolean;
  /** True while a drag is on: one step back for the whole drag, and no dry run per pixel. */
  dragging: boolean;
};

export type Action =
  | { type: "load"; stamp: string; base: TimetableDraft; month: string; restore?: { draft: TimetableDraft; history: Step[] } }
  | { type: "edit"; label: string; change: (draft: TimetableDraft) => TimetableDraft; touched?: string; select?: string | null; last?: string }
  | { type: "drag-start"; label: string; key: string }
  | { type: "drag-move"; item: DraftItem }
  | { type: "drag-end"; changed: boolean }
  | { type: "undo" }
  | { type: "undo-all" }
  | { type: "view"; view: EditorState["view"] }
  | { type: "brush"; brush: EditorState["brush"] }
  | { type: "select"; key: string | null }
  | { type: "adding"; adding: boolean }
  | { type: "day"; day: string | null }
  | { type: "month"; month: string }
  | { type: "review"; open: boolean }
  /** Esc: closes the open block, else stops adding times. */
  | { type: "escape" };

const MAX_STEPS = 60;

export function initialState(stamp: string, base: TimetableDraft, month: string, restore?: { draft: TimetableDraft; history: Step[] }): EditorState {
  return {
    stamp,
    base,
    draft: restore?.draft ?? base,
    history: restore?.history ?? [],
    view: "week",
    brush: null,
    selected: null,
    last: null,
    touched: null,
    adding: false,
    day: null,
    month,
    review: false,
    dragging: false,
  };
}

function push(history: Step[], step: Step): Step[] {
  const next = [...history, step];
  return next.length > MAX_STEPS ? next.slice(next.length - MAX_STEPS) : next;
}

export function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case "load":
      return initialState(action.stamp, action.base, action.month, action.restore);
    case "edit": {
      const draft = action.change(state.draft);
      if (draft === state.draft) return state;
      return {
        ...state,
        draft,
        history: push(state.history, { label: action.label, draft: state.draft }),
        touched: action.touched ?? state.touched,
        selected: action.select === undefined ? state.selected : action.select,
        last: action.last ?? state.last,
      };
    }
    case "drag-start":
      return {
        ...state,
        dragging: true,
        selected: action.key,
        touched: action.key,
        adding: false,
        history: push(state.history, { label: action.label, draft: state.draft }),
      };
    case "drag-move":
      return {
        ...state,
        draft: { ...state.draft, items: state.draft.items.map((item) => (item.key === action.item.key ? action.item : item)) },
      };
    case "drag-end":
      // A drag that ended where it started leaves no step to undo.
      return action.changed ? { ...state, dragging: false } : { ...state, dragging: false, history: state.history.slice(0, -1) };
    case "undo": {
      const last = state.history[state.history.length - 1];
      if (!last) return state;
      const selected = state.selected && last.draft.items.some((item) => item.key === state.selected) ? state.selected : null;
      return { ...state, draft: last.draft, history: state.history.slice(0, -1), selected };
    }
    case "undo-all":
      if (state.draft === state.base) return state;
      return {
        ...state,
        draft: state.base,
        history: push(state.history, { label: "Cancel", draft: state.draft }),
        selected: null,
        day: null,
      };
    case "view":
      return { ...state, view: action.view, selected: null, brush: null, last: null, adding: false, day: null };
    case "brush":
      // Another level forgets the last time; another coach for the same level keeps it.
      return {
        ...state,
        brush: action.brush,
        selected: null,
        adding: false,
        last: action.brush && state.brush && action.brush.groupId === state.brush.groupId ? state.last : null,
      };
    case "select":
      // Opening a block keeps the level picked: the card closes and the owner goes on adding.
      return { ...state, selected: action.key, adding: false, touched: action.key ?? state.touched };
    case "adding":
      return { ...state, adding: action.adding, selected: null, brush: null, last: null };
    case "day":
      return { ...state, day: action.day };
    case "month":
      return { ...state, month: action.month };
    case "review":
      return { ...state, review: action.open };
    case "escape":
      return state.selected ? { ...state, selected: null } : { ...state, brush: null, last: null };
  }
}

// ---------------------------------------------------------------------------
// The draft while the owner is on another page
// ---------------------------------------------------------------------------

type Saved = { stamp: string; draft: TimetableDraft; history: Step[] };

const saved = new Map<string, Saved>();

// The draft is also kept in this browser's storage, so a reload or a closed tab does not lose it.
// Storage can be missing or full (a private window): the draft then lives only in the page.
const STORAGE_PREFIX = "teamna-timetable-draft:";

const isText = (value: unknown) => typeof value === "string";
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

function isItem(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.key) &&
    isText(value.groupId) &&
    typeof value.free === "boolean" &&
    // Weekdays 0 to 6, each once: a day twice would publish two rows under one block.
    Array.isArray(value.days) &&
    value.days.every((day) => Number.isInteger(day) && (day as number) >= 0 && (day as number) <= 6) &&
    new Set(value.days).size === value.days.length &&
    Number.isInteger(value.start) &&
    Number.isInteger(value.end) &&
    (value.start as number) >= 0 &&
    (value.start as number) < (value.end as number) &&
    (value.end as number) <= 24 * 60 &&
    (value.capacity === null || (Number.isInteger(value.capacity) && (value.capacity as number) > 0)) &&
    (value.staffUserId === null || isText(value.staffUserId)) &&
    // The session name and its line: absent in a draft stored before names.
    (value.name === undefined || value.name === null || isText(value.name)) &&
    (value.detail === undefined || value.detail === null || isText(value.detail)) &&
    isRecord(value.ids) &&
    Object.values(value.ids).every(isText) &&
    // A copy of the month before names the rows it came from (`from`).
    (value.from === undefined || (isRecord(value.from) && Object.values(value.from).every(isText)))
  );
}

function isDraft(value: unknown): value is TimetableDraft {
  return (
    isRecord(value) &&
    Array.isArray(value.items) &&
    value.items.every(isItem) &&
    (value.until === null || isText(value.until)) &&
    Array.isArray(value.closures) &&
    value.closures.every((row) => isRecord(row) && isText(row.date) && (row.item === null || isText(row.item)) && (row.reason === null || isText(row.reason)))
  );
}

/**
 * The row ids of a kept draft tell the server which weekly rows (and bookings) stay, so they must be
 * true: each id is a row of the published timetable, of the same level and kind (a class or private
 * hours), under the same key it has
 * there (the weekday, or `weekday@minute` for private hours: the editor never changes a key), and
 * named once in the draft.
 */
function rowsTrue(draft: TimetableDraft, base: TimetableDraft): boolean {
  const rows = new Map<string, { groupId: string; key: string; free: boolean }>();
  for (const item of base.items) for (const [key, id] of Object.entries(item.ids)) rows.set(id, { groupId: item.groupId, key, free: item.free });
  const seen = new Set<string>();
  for (const item of draft.items) {
    for (const [key, id] of Object.entries(item.ids)) {
      const row = rows.get(id);
      if (!row || row.groupId !== item.groupId || row.free !== item.free || row.key !== key || seen.has(id)) return false;
      seen.add(id);
    }
  }
  return true;
}

/**
 * The kept draft, when it has the shape this page writes and its rows are rows of `base`. Anything
 * else (an older page, a broken value) is removed.
 */
function readStored(businessId: string, base: TimetableDraft): Saved | undefined {
  const key = STORAGE_PREFIX + businessId;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const value: unknown = JSON.parse(raw);
    const good =
      isRecord(value) &&
      isText(value.stamp) &&
      isDraft(value.draft) &&
      rowsTrue(value.draft, base) &&
      Array.isArray(value.history) &&
      value.history.every((step) => isRecord(step) && isText(step.label) && isDraft(step.draft) && rowsTrue(step.draft, base));
    if (good) return value as Saved;
  } catch {
    // Not JSON, or no storage: removed below when there is storage.
  }
  try {
    localStorage.removeItem(key);
  } catch {
    // No storage.
  }
  return undefined;
}

function writeStored(businessId: string, value: Saved | null): void {
  try {
    if (value) localStorage.setItem(STORAGE_PREFIX + businessId, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_PREFIX + businessId);
  } catch {
    // No storage: the draft stays in the page only.
  }
}

/** The draft kept for this published timetable (`stamp`, the JSON of `base`), if any. */
export function savedDraft(businessId: string, stamp: string, base: TimetableDraft): { draft: TimetableDraft; history: Step[] } | undefined {
  const entry = saved.get(businessId) ?? readStored(businessId, base);
  return entry && entry.stamp === stamp ? { draft: entry.draft, history: entry.history } : undefined;
}

export function saveDraft(businessId: string, state: Pick<EditorState, "stamp" | "draft" | "history" | "base">): void {
  if (state.draft === state.base && state.history.length === 0) {
    saved.delete(businessId);
    writeStored(businessId, null);
    return;
  }
  const entry = { stamp: state.stamp, draft: state.draft, history: state.history };
  saved.set(businessId, entry);
  writeStored(businessId, entry);
}

/** For the tests: what a reload does, the page forgets and the browser's storage stays. */
export function forgetPageDrafts(): void {
  saved.clear();
}

/** For the tests: start every test with no draft. */
export function clearSavedDrafts(): void {
  for (const businessId of saved.keys()) writeStored(businessId, null);
  saved.clear();
  try {
    for (const key of Object.keys(localStorage)) if (key.startsWith(STORAGE_PREFIX)) localStorage.removeItem(key);
  } catch {
    // No storage.
  }
}
